import type { AdvancedCarrierFolderPublication } from '@/services/api';

export const MAX_FOLDER_PUBLICATION_STEPS_PER_ACTION = 25;

export type CarrierFolderPublicationStopReason =
  | 'complete'
  | 'blocked'
  | 'cancelled'
  | 'stalled'
  | 'step_limit';

export interface CarrierFolderPublicationRunResult {
  lastState: AdvancedCarrierFolderPublication | null;
  steps: number;
  stopReason: CarrierFolderPublicationStopReason;
}

export async function runCarrierFolderPublication(input: {
  advance: () => Promise<AdvancedCarrierFolderPublication | null>;
  onProgress: (state: AdvancedCarrierFolderPublication) => Promise<void> | void;
  shouldContinue?: () => boolean;
  maxSteps?: number;
}): Promise<CarrierFolderPublicationRunResult> {
  const shouldContinue = input.shouldContinue ?? (() => true);
  const maxSteps = input.maxSteps ?? MAX_FOLDER_PUBLICATION_STEPS_PER_ACTION;
  let lastState: AdvancedCarrierFolderPublication | null = null;

  for (let steps = 0; steps < maxSteps; steps += 1) {
    if (!shouldContinue()) {
      return { lastState, steps, stopReason: 'cancelled' };
    }
    lastState = await input.advance();
    if (!lastState) {
      return { lastState, steps: steps + 1, stopReason: 'stalled' };
    }
    if (!shouldContinue()) {
      return { lastState, steps: steps + 1, stopReason: 'cancelled' };
    }
    await input.onProgress(lastState);
    if (!lastState.ready) {
      return { lastState, steps: steps + 1, stopReason: 'blocked' };
    }
    if (lastState.pendingCarriers === 0) {
      return { lastState, steps: steps + 1, stopReason: 'complete' };
    }
    if (!lastState.published) {
      return { lastState, steps: steps + 1, stopReason: 'stalled' };
    }
  }

  return {
    lastState,
    steps: maxSteps,
    stopReason: 'step_limit',
  };
}
